import React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { ClockInputStore } from './clock_input_store';
import baseStyles from './clock_input.module.css';
import { Theme, ThemeContext, ThemedStyles } from 'ui/base/theme';
import { ButtonClickEvent } from 'ui/base/types';
import { NumberStore } from 'ui/base/input';
import Messages from './clock_input_messages';

const DIAL_RADIUS = 100;
const HAND_RADIUS = 80;
const TICK_RADIUS = 14;

export function toTwoDigits(number: number) {
  return number.toString().length !== 2 ? '0' + number.toString() : number;
}

export function toTwelveHour(hour: number) {
  return toTwoDigits(hour % 12 || 12);
}

function leadingZero(num: number): string {
  const leftPad = num < 10 ? '0' : '';
  return `${leftPad}${num}`;
}

function computeRadians(plate: HTMLElement, clientX: number, clientY: number): number {
  // Return a number from [0, 2*PI)
  const viewportOffset = plate.getBoundingClientRect();
  const x0 = viewportOffset.left + DIAL_RADIUS;
  const y0 = viewportOffset.top + DIAL_RADIUS;
  const x = clientX - x0;
  const y = clientY - y0;

  let radians = Math.atan2(x, -y);
  if (radians < 0) {
    radians += 2 * Math.PI;
  }

  return radians;
}

function computeDistance(plate: HTMLElement, clientX: number, clientY: number): number {
  const viewportOffset = plate.getBoundingClientRect();
  const x0 = viewportOffset.left + DIAL_RADIUS;
  const y0 = viewportOffset.top + DIAL_RADIUS;
  const x = clientX - x0;
  const y = clientY - y0;

  return Math.sqrt(x * x + y * y);
}

function radiansToHours(radians: number): number {
  // Return a number from 1 to 12
  const units = Math.PI / 6;
  const hours = Math.round(radians / units);
  if (hours === 0) {
    return 12;
  } else {
    return hours;
  }
}

function radiansToMinutes(radians: number, roundByFive: boolean) {
  let value: number;
  if (roundByFive) {
    const units = Math.PI / 6;
    value = Math.round(radians / units) * 5;
  } else {
    const units = Math.PI / 30;
    value = Math.round(radians / units);
  }
  if (value === 60) {
    return 0;
  }
  return value;
}

function hoursToRadians(hours: number): number {
  // Return a number from 0 to 2*Math.PI
  if (hours === 12) {
    return 0;
  }
  return hours / 6 * Math.PI;
}

function minutesToRadians(minutes: number): number {
  // Return a number from 0 to 2*Math.PI
  if (minutes === 60) {
    return 0;
  }
  return minutes / 30 * Math.PI;
}

function hoursToText(hours: number): string {
  return hours.toString();
}

function minutesToText(minutes: number): string {
  return leadingZero(minutes);
}

type ClockPickerStyles = {
  inputButton: string;
  visible: string;
  modal: string;
  backdrop: string;
  modalBody: string;
  modalContent: string;
  header: string;
  currentTime: string;
  currentTimeSeparator: string;
  currentTimeButton: string;
  ampmButton: string;
  activeButton: string;
  ampmPicker: string;
  body: string;
  plate: string;
  dialHours: string;
  dialMinutes: string;
  dialOut: string;
  tick: string;
  canvasCenter: string;
  canvasFG: string;
  canvasBG: string;
  canvasHand: string;
  actions: string;
  actionButton: string;
};

const themedStyles: ThemedStyles<ClockPickerStyles> = {
  [Theme.DAYLIGHT]: {
    inputButton: baseStyles.daylightInputButton,
    visible: baseStyles.visible,
    modal: baseStyles.modal,
    backdrop: baseStyles.backdrop,
    modalBody: baseStyles.daylightModalBody,
    modalContent: baseStyles.modalContent,
    header: baseStyles.header,
    currentTime: baseStyles.currentTime,
    currentTimeSeparator: baseStyles.currentTimeSeparator,
    currentTimeButton: baseStyles.currentTimeButton,
    ampmButton: baseStyles.ampmButton,
    activeButton: baseStyles.activeButton,
    ampmPicker: baseStyles.ampmPicker,
    body: baseStyles.body,
    plate: baseStyles.plate,
    dialHours: baseStyles.dialHours,
    dialMinutes: baseStyles.dialMinutes,
    dialOut: baseStyles.dialOut,
    tick: baseStyles.tick,
    canvasCenter: baseStyles.canvasCenter,
    canvasFG: baseStyles.canvasFG,
    canvasBG: baseStyles.canvasBG,
    canvasHand: baseStyles.canvasHand,
    actions: baseStyles.actions,
    actionButton: baseStyles.actionButton,
  },
  [Theme.MIDNIGHT]: {
    inputButton: baseStyles.midnightInputButton,
    visible: baseStyles.visible,
    modal: baseStyles.modal,
    backdrop: baseStyles.backdrop,
    modalBody: baseStyles.midnightModalBody,
    modalContent: baseStyles.modalContent,
    header: baseStyles.header,
    currentTime: baseStyles.currentTime,
    currentTimeSeparator: baseStyles.currentTimeSeparator,
    currentTimeButton: baseStyles.currentTimeButton,
    ampmButton: baseStyles.ampmButton,
    activeButton: baseStyles.activeButton,
    ampmPicker: baseStyles.ampmPicker,
    body: baseStyles.body,
    plate: baseStyles.plate,
    dialHours: baseStyles.dialHours,
    dialMinutes: baseStyles.dialMinutes,
    dialOut: baseStyles.dialOut,
    tick: baseStyles.tick,
    canvasCenter: baseStyles.canvasCenter,
    canvasFG: baseStyles.canvasFG,
    canvasBG: baseStyles.canvasBG,
    canvasHand: baseStyles.canvasHand,
    actions: baseStyles.actions,
    actionButton: baseStyles.actionButton,
  },
};


type ClockTickProps = {
  text: string;
  top: number;
  left: number;
  onMouseDown(event: React.MouseEvent): void;
  onTouchStart(event: React.TouchEvent): void;
};
@observer
export class ClockTick extends React.Component<ClockTickProps> {
  static contextType = ThemeContext;
  context!: React.ContextType<typeof ThemeContext>;

  render() {
    const styles = themedStyles[this.context];

    const { text, top, left, onMouseDown, onTouchStart } = this.props;
    const style = {
      left,
      top,
    };
    return (
      <div
        className={styles.tick}
        style={style}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        {text}
      </div>
    );

  }
}

type ClockFaceProps = {
  plate: HTMLDivElement;
  active: boolean;
  dialClass: string;
  store: NumberStore;
  radiansToValue(radians: number, applyRounding: boolean): number;
  valueToRadians(value: number): number;
  valueToText(value: number): string;
  onCommit?(): void;
};
@observer
export class ClockFace extends React.Component<ClockFaceProps> {
  static contextType = ThemeContext;
  context!: React.ContextType<typeof ThemeContext>;

  isPointerDown: boolean;

  @observable.ref
  radians: number;

  constructor(props: ClockFaceProps) {
    super(props);
    this.radians = 0;
    this.isPointerDown = false;
  }

  componentWillUnmount() {
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('mousemove', this.onMouseMove);
  }

  resetClock() {
    this.radians = this.props.valueToRadians(this.props.store.value);
  }

  onGenericMove(clientX: number, clientY: number) {
    const unroundedRadians = computeRadians(this.props.plate, clientX, clientY);
    const value = this.props.radiansToValue(unroundedRadians, false);
    this.radians = this.props.valueToRadians(value);
    this.props.store.setValue(value);
  }

  @action.bound
  onTouchMove(event: TouchEvent) {
    event.preventDefault();
    event.stopPropagation();

    const target = event.touches[0];
    this.onGenericMove(target.clientX, target.clientY);
  }

  @action.bound
  onMouseMove(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.onGenericMove(event.clientX, event.clientY);
  }

  onGenericDown(clientX: number, clientY: number, applyRounding: boolean) {
    if (!this.props.active) {
      return;
    }

    const distance = computeDistance(this.props.plate, clientX, clientY);
    if (distance < HAND_RADIUS - TICK_RADIUS || distance > HAND_RADIUS + TICK_RADIUS) {
      return;
    }

    const unroundedRadians = computeRadians(this.props.plate, clientX, clientY);
    const value = this.props.radiansToValue(unroundedRadians, applyRounding);
    this.radians = this.props.valueToRadians(value);
    this.props.store.setValue(value);
  }

  @action.bound
  onTouchStart(event: React.TouchEvent, applyRounding?: boolean) {
    event.preventDefault();
    event.stopPropagation();

    const target = event.touches[0];
    this.onGenericDown(target.clientX, target.clientY, applyRounding ?? false);

    this.isPointerDown = true;
    window.addEventListener('touchmove', this.onTouchMove, { passive: false });
    window.addEventListener('touchend', this.onTouchEnd);
  }

  @action.bound
  onTouchStartRounded(event: React.TouchEvent) {
    this.onTouchStart(event, true);
  }

  @action.bound
  onMouseDown(event: React.MouseEvent, applyRounding?: boolean) {
    event.preventDefault();
    event.stopPropagation();

    this.onGenericDown(event.clientX, event.clientY, applyRounding ?? false);

    this.isPointerDown = true;
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
  }

  @action.bound
  onMouseDownRounded(event: React.MouseEvent) {
    this.onMouseDown(event, false);
  }

  @action.bound
  onTouchEnd() {
    window.removeEventListener('touchmove', this.onTouchMove);
    window.removeEventListener('touchend', this.onTouchEnd);
    this.isPointerDown = false;
    if (this.props.onCommit != null) {
      this.props.onCommit();
    }
  }

  @action.bound
  onMouseUp() {
    window.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.isPointerDown = false;
    if (this.props.onCommit != null) {
      this.props.onCommit();
    }
  }

  renderSVG() {
    const DIAMETER = DIAL_RADIUS * 2;
    const cx = Math.sin(this.radians) * HAND_RADIUS;
    const cy = -Math.cos(this.radians) * HAND_RADIUS;

    let display: React.ReactNode;

    const unroundedValue = this.props.radiansToValue(this.radians, false);
    const roundedValue = this.props.radiansToValue(this.radians, true);

    const styles = themedStyles[this.context];

    if (unroundedValue === roundedValue) {
      // If we have a nice round number
      display = (
        <>
          <circle className={styles.canvasCenter} cx='0' cy='0' r='2'/>
          <line className={styles.canvasHand} x1='0' y1='0' x2={cx} y2={cy}/>
          <circle className={styles.canvasBG} cx={cx} cy={cy} r={TICK_RADIUS}/>
        </>
      );
    } else {
      // Intentionally draw the line after the bg
      display = (
        <>
          <circle className={styles.canvasCenter} cx='0' cy='0' r='2'/>
          <circle className={styles.canvasBG} cx={cx} cy={cy} r={TICK_RADIUS}/>
          <line className={styles.canvasHand} x1='0' y1='0' x2={cx} y2={cy}/>
          <circle className={styles.canvasFG} cx={cx} cy={cy} r={3.5}/>
        </>
      );
    }

    return (
      <svg className='clockpicker-svg' width={DIAMETER} height={DIAMETER}>
        <g transform={`translate(${DIAL_RADIUS},${DIAL_RADIUS})`}>
          {display}
        </g>
      </svg>
    );
  }

  renderTicks(): React.ReactNode {
    const ticks: React.ReactElement[] = [];
    for (let i = 0; i < 12; i++) {
      const radians = i / 6 * Math.PI;
      const left = DIAL_RADIUS - TICK_RADIUS + Math.sin(radians) * HAND_RADIUS;
      const top = DIAL_RADIUS - TICK_RADIUS - Math.cos(radians) * HAND_RADIUS;
      const value = this.props.radiansToValue(radians, true);
      const text = this.props.valueToText(value);
      const tick = (
        <ClockTick
          key={text}
          text={text}
          top={top}
          left={left}
          onTouchStart={this.onTouchStartRounded}
          onMouseDown={this.onMouseDownRounded}
        />
      );
      ticks.push(tick);
    }
    return ticks;
  }

  render() {
    const styles = themedStyles[this.context];

    return (
      <div
        className={classNames(
          this.props.dialClass,
          { [styles.dialOut]: !this.props.active }
        )}
        onMouseDown={this.onMouseDown}
        onTouchStart={this.onTouchStart}
      >
        {this.renderSVG()}
        {this.renderTicks()}
      </div>
    );
  }
}

type ClockInputProps = {
  store: ClockInputStore;
};
@observer
export class ClockInput extends React.Component<ClockInputProps> {
  static contextType = ThemeContext;
  context!: React.ContextType<typeof ThemeContext>;

  @observable.ref
  plate: HTMLDivElement | null;
  hours: ClockFace | null;
  minutes: ClockFace | null;

  @observable.ref
  currentView: 'hours' | 'minutes';

  constructor(props: ClockInputProps) {
    super(props);
    this.plate = null;
    this.hours = null;
    this.minutes = null;
    this.currentView = 'hours';
    this.resetClock();
  }

  @action.bound
  resetClock() {
    this.hours?.resetClock();
    this.minutes?.resetClock();
  }

  @action.bound
  setRefPlate(plate: HTMLDivElement) {
    this.plate = plate;
  }

  @action.bound
  setRefHours(hours: ClockFace) {
    this.hours = hours;
  }

  @action.bound
  setRefMinutes(minutes: ClockFace) {
    this.minutes = minutes;
  }

  @action.bound
  switchToHours() {
    this.currentView = 'hours';
  }

  @action.bound
  switchToMinutes() {
    this.currentView = 'minutes';
  }

  @action.bound
  onShowModal(event: ButtonClickEvent) {
    event.stopPropagation();
    const store = this.props.store;
    store.reloadTime();
    this.switchToHours();
    this.resetClock();
    this.currentView = 'hours';
    store.modal.showModal();
  }

  @action.bound
  commitTime() {
    const store = this.props.store;
    store.commitTime();
    store.modal.hideModal();
  }

  @action.bound
  switchToAM() {
    this.props.store.tempAMPM.setValue('AM');
  }

  @action.bound
  switchToPM() {
    this.props.store.tempAMPM.setValue('PM');
  }

  ModalContent = observer(() => {
    const store = this.props.store;
    const styles = themedStyles[this.context];

    return (
      <>
        <div className={styles.header}>
          <div className={styles.currentTime}>
            <button type='button' className={classNames(styles.currentTimeButton, this.currentView === 'hours' && styles.activeButton)} onClick={this.switchToHours}>
              {leadingZero(store.tempHours.value)}
            </button>
            <div className={styles.currentTimeSeparator}>
              :
            </div>
            <button type='button' className={classNames(styles.currentTimeButton, this.currentView === 'minutes' && styles.activeButton)} onClick={this.switchToMinutes}>
              {leadingZero(store.tempMinutes.value)}
            </button>
          </div>
          <div className={styles.ampmPicker}>
            <button type='button' className={classNames(styles.ampmButton, store.tempAMPM.value === 'AM' && styles.activeButton)} onClick={this.switchToAM}>
              {Messages.AM()}
            </button>
            <button type='button' className={classNames(styles.ampmButton, store.tempAMPM.value === 'PM' && styles.activeButton)} onClick={this.switchToPM}>
              {Messages.PM()}
            </button>
          </div>
        </div>
        <div className={styles.body}>
          <div ref={this.setRefPlate} className={styles.plate}>
            {this.plate &&
              <ClockFace
                plate={this.plate}
                dialClass={styles.dialHours}
                active={this.currentView === 'hours'}
                store={store.tempHours}
                radiansToValue={radiansToHours}
                valueToRadians={hoursToRadians}
                valueToText={hoursToText}
                onCommit={this.switchToMinutes}
                ref={this.setRefHours}
              />
            }
            {this.plate &&
              <ClockFace
                plate={this.plate}
                dialClass={styles.dialMinutes}
                active={this.currentView === 'minutes'}
                store={store.tempMinutes}
                radiansToValue={radiansToMinutes}
                valueToRadians={minutesToRadians}
                valueToText={minutesToText}
                onCommit={this.switchToMinutes}
                ref={this.setRefMinutes}
              />
            }
          </div>
          <div className={styles.actions}>
            <button type='button' className={styles.actionButton} onClick={store.modal.hideModal}>
              {Messages.cancel()}
            </button>
            <button type='button' className={styles.actionButton} onClick={this.commitTime}>
              {Messages.ok()}
            </button>
          </div>
        </div>
      </>
    );
  });

  ClockInputModal = observer(() => {
    const styles = themedStyles[this.context];

    return (
      <div className={classNames(styles.modal, { [styles.visible]: this.props.store.modal.visible })}>
        <div className={styles.backdrop} onClick={this.props.store.modal.hideModal}/>
        <div className={styles.modalBody}>
          <div className={styles.modalContent}>
            <this.ModalContent/>
          </div>
        </div>
      </div>
    );
  });

  render() {
    const styles = themedStyles[this.context];

    const hourDisplay = toTwelveHour(this.props.store.hours.value);
    const minsDisplay = toTwoDigits(this.props.store.minutes.value);
    const ampmDisplay = this.props.store.hours.value < 12 ? 'AM' : 'PM';
    return (
      <>
        <button type='button' className={styles.inputButton} onClick={this.onShowModal}>
          {`${hourDisplay}:${minsDisplay} ${ampmDisplay}`}
        </button>
        <this.ClockInputModal/>
      </>
    );
  }
}
